// frontend/src/pages/Dashboard.tsx

import { useAuthStore } from '../store/authStore';
import { useDashboardData } from '../hooks/useDashboardData';
import { useChallenges } from '../hooks/useChallenges';
import { TodayPlanCard } from '../components/dashboard/TodayPlanCard';
import { ProgressDonutCard } from '../components/dashboard/ProgressDonutCard';
import { RecentActivityCard } from '../components/dashboard/RecentActivityCard';
import { ContinueLearningCard } from '../components/dashboard/ContinueLearningCard';
import { SuggestedCoursesCard } from '../components/dashboard/SuggestedCoursesCard';
import { AIAssistantWidget } from '../components/dashboard/AIAssistantWidget';
import { UsageBanner } from '../components/common/UsageBanner';
import '../styles/dashboard.css';

export function DashboardPage() {
  const profile = useAuthStore((s) => s.profile);
  const { data, isLoading, error } = useDashboardData();
  const { challenges, complete } = useChallenges();

  async function handleCompleteChallenge(challengeId: string) {
    await complete(challengeId);
  }

  if (isLoading) {
    return (
      <div className="nd-loading">
        <div className="nd-loading-spinner" />
        <span>Yükleniyor...</span>
      </div>
    );
  }

  if (error) {
    return <div className="nd-error">Bir şeyler ters gitti: {error}</div>;
  }

  if (!data || !profile) return null;

  // Genel ilerleme yüzdesi
  const totalLessons = data.activeCourses.reduce((sum, c) => sum + c.totalLessons, 0);
  const completedLessons = data.stats.completedLessons;
  const overallPercent = totalLessons > 0
    ? Math.min(100, Math.round((completedLessons / Math.max(totalLessons, completedLessons + 1)) * 100))
    : Math.min(72, completedLessons * 2);

  const firstName = profile.full_name?.split(' ')[0] || 'Öğrenci';

  // En yüksek ilerleme oranındaki aktif ders (Devam Et için)
  const continueLesson = data.activeCourses.length > 0
    ? [...data.activeCourses].sort((a, b) => b.progressPercent - a.progressPercent)[0]
    : null;

  return (
    <div className="nd-page">
      {/* Header */}
      <header className="nd-header">
        <div>
          <div className="nd-header-badge">
            <span className="nd-header-badge-dot"></span>
            ÖĞRENİM MERKEZİ
          </div>
          <h1 className="nd-header-title">Merhaba {firstName}! 👋</h1>
          <p className="nd-header-sub">Bugün yeni şeyler keşfederek kendini geliştirme zamanı!</p>
        </div>
        <div className="nd-header-xp">
          <span>⭐</span>
          <span className="nd-header-xp-value">{profile.xp.toLocaleString('tr-TR')} XP</span>
        </div>
      </header>

      <UsageBanner type="lesson" />
      <UsageBanner type="ai_message" />
      <UsageBanner type="project" />

      {/* Üst 3 panel */}
      <div className="nd-top-grid">
        <TodayPlanCard courses={data.activeCourses} />
        <ProgressDonutCard
          completedLessons={completedLessons}
          totalXp={profile.xp}
          badges={data.stats.completedProjects}
          overallPercent={overallPercent}
        />
        <RecentActivityCard items={data.recentActivity} />
      </div>

      {/* Devam Et */}
      <ContinueLearningCard course={continueLesson} />

      {/* Alt: Önerilen Dersler + AI Widget */}
      <div className="nd-bottom-grid">
        <SuggestedCoursesCard
          courses={data.suggestedCourses}
          age={profile.age}
        />
        <AIAssistantWidget activeCourses={data.activeCourses} />
      </div>
    </div>
  );
}
